// For listening stories: only audio resources are allowed (transcript required).
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Edit, FileText, Headphones, Video } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { IconButton } from '@/components/atoms/IconButton';
import { Badge } from '@/components/atoms/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/molecules/Card';
import { useResources, useStory } from '@/hooks/useBackofficeData';
import { buildRoute, ROUTES } from '@/constants/routes';
import styles from './ResourceEditorPage.module.scss';

const RESOURCE_TYPE_ICONS: Record<string, React.ReactNode> = {
  text: <FileText size={16} />,
  audio: <Headphones size={16} />,
  video: <Video size={16} />,
};

const RESOURCE_TYPE_LABELS: Record<string, string> = {
  text: 'Texto',
  audio: 'Audio',
  video: 'Vídeo',
};

function formatDuration(seconds: number): string {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function ResourceEditorPage() {
  const navigate = useNavigate();
  const { storyId: storyId } = useParams<{ storyId: string }>();
  const { story } = useStory(storyId);
  const { resources, isLoading, deleteResource } = useResources(storyId);

  const storyType = story?.type ?? 'general';

  const handleAddResource = () => {
    if (storyId) {
      navigate(buildRoute(ROUTES.BACKOFFICE.STORY_RESOURCE_CREATE, { storyId: storyId }));
    }
  };

  const handleEditResource = (resourceId: string) => {
    if (storyId) {
      navigate(buildRoute(ROUTES.BACKOFFICE.STORY_RESOURCE_EDIT, { storyId: storyId, resourceId }));
    }
  };

  const handleDeleteResource = async (resourceId: string) => {
    if (window.confirm('¿Seguro que quieres eliminar este recurso?')) {
      try {
        await deleteResource(resourceId);
      } catch (error) {
        console.error('Error deleting resource:', error);
        alert('Error al eliminar el recurso');
      }
    }
  };

  const handleBack = () => {
    if (storyId) {
      navigate(buildRoute(ROUTES.BACKOFFICE.STORY_EDIT, { id: storyId }));
    } else {
      navigate(-1);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.resourceEditor}>
        <div className={styles.resourceEditor__loading}>Cargando recursos...</div>
      </div>
    );
  }

  return (
    <div className={styles.resourceEditor}>
      <div className={styles.resourceEditor__header}>
        <IconButton icon={ArrowLeft} onClick={handleBack} aria-label="Volver a la historia" />
        <Button leftIcon={<Plus />} onClick={handleAddResource}>
          Añadir recurso
        </Button>
      </div>

      <div>
        <h1 className={styles.resourceEditor__title}>Editor de recursos</h1>
        <p className={styles.resourceEditor__subtitle}>
          ID historia: {storyId} · {resources.length}{' '}
          {resources.length === 1 ? 'recurso' : 'recursos'}
          {storyType === 'listening' && (
            <span className={styles.resourceEditor__typeNotice}>
              {' '}· Escucha — audio con transcript obligatorio
            </span>
          )}
        </p>
      </div>

      <div className={styles.resourceEditor__content}>
        {resources.length === 0 ? (
          <Card>
            <CardContent className={styles.resourceEditor__empty}>
              <p>Sin recursos. Haz clic en "Añadir recurso" para crear uno.</p>
            </CardContent>
          </Card>
        ) : (
          <div className={styles.resourceEditor__list}>
            {resources.map((resource) => (
              <Card key={resource.id} className={styles.resourceEditor__item}>
                <CardHeader className={styles.resourceEditor__itemHeader}>
                  <div className={styles.resourceEditor__itemInfo}>
                    <div className={styles.resourceEditor__itemBadges}>
                      <Badge variant="default">
                        <span className={styles.resourceEditor__badgeIcon}>
                          {RESOURCE_TYPE_ICONS[resource.type]}
                        </span>
                        {RESOURCE_TYPE_LABELS[resource.type]}
                      </Badge>
                      {resource.duration ? (
                        <Badge variant="info">{formatDuration(resource.duration)}</Badge>
                      ) : null}
                      {resource.transcript && (
                        <Badge variant="success">Transcript</Badge>
                      )}
                      <span className={styles.resourceEditor__itemOrder}>Orden: {resource.order}</span>
                    </div>
                    <CardTitle>{resource.title}</CardTitle>
                    {resource.type === 'text' && resource.content && (
                      <p className={styles.resourceEditor__itemPreview}>
                        {resource.content.slice(0, 120)}
                        {resource.content.length > 120 ? '…' : ''}
                      </p>
                    )}
                    {(resource.type === 'audio' || resource.type === 'video') && resource.url && (
                      <p className={styles.resourceEditor__itemPreview}>{resource.url}</p>
                    )}
                  </div>
                  <div className={styles.resourceEditor__itemActions}>
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Edit />}
                      onClick={() => handleEditResource(resource.id)}
                    >
                      Editar
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      leftIcon={<Trash2 />}
                      onClick={() => handleDeleteResource(resource.id)}
                    >
                      Eliminar
                    </Button>
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
